import React, { useMemo, useState, type ReactNode } from "react";
import clsx from "clsx";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
} from "lucide-react";

import type { ResponsiveColumn, SortDirection } from "./types.ts";
import styles from "./responsive-table.module.css";
import { TruncatedCellTooltip } from "./truncated-cell-tooltip.tsx";
import { capitalizeWords } from "../../../utils/format-display-text.ts";

export type { ResponsiveColumn, SortDirection } from "./types.ts";

type PaginationControls = {
  pagination?: true;
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: readonly number[];
};

type ResponsiveDataTableBaseProps<T extends Record<string, unknown>> = {
  columns: ResponsiveColumn<T>[];
  data: T[];
  sortKey?: string;
  sortDir?: SortDirection;
  onSortChange?: (sortKey: string | undefined, sortDir: SortDirection) => void;
  defaultSortKey?: string;
  defaultSortDir?: Exclude<SortDirection, null>;
  serialNumber?: boolean;
  className?: string;
  "aria-label"?: string;
  emptyState?: ReactNode;
  /** `<tfoot>` row(s): `<tr>…</tr>` with `<td>` / `<th>`. */
  tableFooter?: ReactNode;
  /** Shown under mobile cards when the `<table>` is hidden. */
  mobileSummary?: ReactNode;
  /**
   * When set, the desktop table section scrolls vertically up to this height
   * and the header row stays sticky inside that region.
   */
  scrollContainerMaxHeight?: string;
  totalLabel?: ReactNode;
  selection?: {
    getRowId: (row: T) => string | number;
    isRowDisabled?: (row: T) => boolean;
    selectedRowIds: ReadonlySet<string | number>;
    onSelectionChange: (selectedRowIds: Set<string | number>) => void;
    ariaLabel?: string;
  };
};

/** Pass `pagination: false` to hide the bottom bar; omit `page` / `onPageChange` / etc. in that case. */
export type ResponsiveDataTableProps<T extends Record<string, unknown>> =
  | (ResponsiveDataTableBaseProps<T> & PaginationControls)
  | (ResponsiveDataTableBaseProps<T> & { pagination: false });

function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function nextSortState(
  columnId: string,
  sortKey: string | undefined,
  sortDir: SortDirection,
): { sortKey: string | undefined; sortDir: SortDirection } {
  if (sortKey !== columnId) {
    return { sortKey: columnId, sortDir: "asc" };
  }
  if (sortDir === "asc") {
    return { sortKey: columnId, sortDir: "desc" };
  }
  if (sortDir === "desc") {
    return { sortKey: undefined, sortDir: null };
  }
  return { sortKey: columnId, sortDir: "asc" };
}

function titleTextForColumn<T extends Record<string, unknown>>(
  col: ResponsiveColumn<T>,
  row: T,
): string {
  if (col.getTitleText) return col.getTitleText(row);
  if (col.accessor != null) {
    const v = row[col.accessor];
    if (v == null) return "";
    return String(v);
  }
  return "";
}

function resolvePagination<T extends Record<string, unknown>>(
  props: ResponsiveDataTableProps<T>,
) {
  if (props.pagination === false) {
    return {
      page: 1,
      pageSize: Math.max(1, props.data.length || 1),
      totalItems: props.data.length,
      onPageChange: (_p: number) => { },
      onPageSizeChange: undefined as ((n: number) => void) | undefined,
      pageSizeOptions: [5, 10, 20, 30, 40] as readonly number[],
    };
  }
  return {
    page: props.page,
    pageSize: props.pageSize,
    totalItems: props.totalItems,
    onPageChange: props.onPageChange,
    onPageSizeChange: props.onPageSizeChange,
    pageSizeOptions: props.pageSizeOptions ?? [5, 10, 20, 30, 40],
  };
}

export function ResponsiveDataTable<T extends Record<string, unknown>>(
  props: ResponsiveDataTableProps<T>,
) {
  const {
    columns,
    data,
    sortKey: sortKeyProp,
    sortDir: sortDirProp,
    onSortChange,
    defaultSortKey,
    defaultSortDir,
    serialNumber = false,
    className,
    "aria-label": ariaLabel,
    emptyState,
    tableFooter,
    mobileSummary,
    scrollContainerMaxHeight,
    totalLabel,
    selection,
  } = props;

  const showPagination = props.pagination !== false;
  const {
    page,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions,
  } = resolvePagination(props);
  const uncontrolled = onSortChange == null;
  const [internalSortKey, setInternalSortKey] = useState<string | undefined>(
    () => defaultSortKey,
  );
  const [internalSortDir, setInternalSortDir] = useState<SortDirection>(
    () => defaultSortDir ?? null,
  );

  const sortKey = uncontrolled ? internalSortKey : sortKeyProp;
  const sortDir = uncontrolled
    ? internalSortDir
    : ((sortDirProp ?? null) as SortDirection);

  const displayData = useMemo(() => {
    // Sort whenever a column + direction are active (controlled or uncontrolled).
    // Previously controlled mode skipped sorting and expected the parent to reorder
    // `data`, but callers like HolidaysTable only mirror sortKey/sortDir in state.
    if (!sortKey || sortDir == null) {
      return data;
    }
    const col = columns.find((c) => c.id === sortKey);
    const getVal = (row: T) =>
      col?.getSortValue != null
        ? col.getSortValue(row)
        : col?.accessor != null
          ? row[col.accessor]
          : null;
    if (col?.getSortValue == null && col?.accessor == null) {
      return data;
    }
    if (!col?.sortable) {
      return data;
    }
    const copy = [...data];
    copy.sort((a, b) => {
      const cmp = compareValues(getVal(a), getVal(b));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [data, columns, sortKey, sortDir]);

  const onHeaderSort = (col: ResponsiveColumn<T>) => {
    if (!col.sortable) return;
    if (uncontrolled && col.accessor == null && col.getSortValue == null) {
      return;
    }

    const { sortKey: nextKey, sortDir: nextDir } = nextSortState(
      col.id,
      sortKey,
      sortDir,
    );

    if (uncontrolled) {
      setInternalSortKey(nextKey);
      setInternalSortDir(nextDir);
    } else {
      onSortChange?.(nextKey, nextDir);
    }
  };

  const offset = (page - 1) * pageSize;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const canPrev = safePage > 1;
  const canNext = safePage < totalPages;

  const renderCellContent = (
    col: ResponsiveColumn<T>,
    row: T,
    index: number,
    truncatedClassName: string,
  ) => {
    const inner = col.render
      ? col.render(row, index)
      : col.accessor != null
        ? (() => {
            const raw = row[col.accessor];
            if (
              col.formatAccessor === "capitalizeWords" &&
              typeof raw === "string"
            ) {
              return (
                <span className="body-sm">{capitalizeWords(raw)}</span>
              );
            }
            return raw as ReactNode;
          })()
        : null;

    if (col.truncate) {
      const title = titleTextForColumn(col, row);
      return (
        <TruncatedCellTooltip
          tooltipText={title}
          truncatedClassName={truncatedClassName}
        >
          {inner}
        </TruncatedCellTooltip>
      );
    }
    return inner;
  };

  const mobileColumns = columns.filter((c) => !c.hiddenOnMobile);
  const pageRowIds = displayData
    .filter((row) => !selection?.isRowDisabled?.(row))
    .map((row) => selection?.getRowId(row));
  const allPageRowsSelected = selection != null && pageRowIds.length > 0 && pageRowIds.every((id) => id != null && selection.selectedRowIds.has(id));
  const toggleAllPageRows = () => {
    if (!selection) return;
    const next = new Set(selection.selectedRowIds);
    pageRowIds.forEach((id) => {
      if (id == null) return;
      if (allPageRowsSelected) next.delete(id); else next.add(id);
    });
    selection.onSelectionChange(next);
  };
  const toggleRow = (row: T) => {
    if (!selection || selection.isRowDisabled?.(row)) return;
    const id = selection.getRowId(row);
    const next = new Set(selection.selectedRowIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    selection.onSelectionChange(next);
  };

  const showEmpty = displayData.length === 0 && emptyState != null;
  const showPaginationBar =
    totalLabel != null || (showPagination && totalItems > 0 && !showEmpty);

  return (
    <>
      {showEmpty ? (
        emptyState
      ) : (
        <div className={clsx(styles.root, className)}>
          <div
            className={clsx(
              styles.tableSection,
              scrollContainerMaxHeight != null && styles.tableSectionScroll,
            )}
            style={
              scrollContainerMaxHeight != null
                ? { maxHeight: scrollContainerMaxHeight }
                : undefined
            }
          >
            <table className={styles.table} aria-label={ariaLabel}>
              <thead
                className={clsx(
                  styles.thead,
                  scrollContainerMaxHeight != null && styles.theadSticky,
                )}
              >
                <tr className={styles.tr}>
                  {selection ? <th scope="col" className={clsx(styles.th, styles.thSelect)}><input type="checkbox" aria-label={selection.ariaLabel ?? "Select all rows on this page"} checked={allPageRowsSelected} onChange={toggleAllPageRows} /></th> : null}
                  {serialNumber ? (
                    <th scope="col" className={clsx(styles.th, styles.thIndex)}>
                      S.No.
                    </th>
                  ) : null}
                  {columns.map((col) => {
                    const active = sortKey === col.id;
                    const dir = active ? sortDir : null;
                    const ariaSort =
                      dir === "asc"
                        ? "ascending"
                        : dir === "desc"
                          ? "descending"
                          : "none";

                    if (!col.sortable) {
                      return (
                        <th
                          key={col.id}
                          scope="col"
                          className={clsx(
                            styles.th,
                            col.nowrap && styles.columnNoWrap,
                          )}
                        >
                          <span className={styles.thLabel}>{col.header}</span>
                        </th>
                      );
                    }

                    return (
                      <th
                        key={col.id}
                        scope="col"
                        aria-sort={ariaSort}
                        className={clsx(
                          styles.th,
                          styles.thSortable,
                          col.nowrap && styles.columnNoWrap,
                        )}
                      >
                        <button
                          type="button"
                          className={styles.sortButton}
                          onClick={() => onHeaderSort(col)}
                        >
                          <span className={styles.thLabel}>{col.header}</span>
                          <span
                            className={clsx(
                              styles.sortIcon,
                              dir && styles.sortIconActive,
                            )}
                            aria-hidden
                          >
                            {dir === "asc" ? (
                              <ChevronUp size={14} strokeWidth={1.75} />
                            ) : dir === "desc" ? (
                              <ChevronDown size={14} strokeWidth={1.75} />
                            ) : (
                              <ChevronsUpDown size={14} strokeWidth={1.75} />
                            )}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className={styles.tbody}>
                {displayData.map((row, rowIndex) => (
                  <tr key={rowIndex} className={styles.tr}>
                    {selection ? <td className={clsx(styles.td, styles.tdSelect)}><input type="checkbox" aria-label={`Select row ${rowIndex + 1}`} checked={selection.selectedRowIds.has(selection.getRowId(row))} disabled={selection.isRowDisabled?.(row)} onChange={() => toggleRow(row)} /></td> : null}
                    {serialNumber ? (
                      <td className={clsx(styles.td, "body-sm")}>
                        {offset + rowIndex + 1}
                      </td>
                    ) : null}
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        className={clsx(
                          styles.td,
                          "body-sm",
                          col.nowrap && styles.columnNoWrap,
                        )}
                      >
                        {renderCellContent(
                          col,
                          row,
                          rowIndex,
                          styles.truncated,
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {tableFooter ? (
                <tfoot className={styles.tfoot}>{tableFooter}</tfoot>
              ) : null}
            </table>
          </div>

          <div className={styles.cardList}>
            {displayData.map((row, rowIndex) => (
              <div key={rowIndex} className={styles.card}>
                {selection ? <div className={styles.cardHeader}><label><input type="checkbox" checked={selection.selectedRowIds.has(selection.getRowId(row))} disabled={selection.isRowDisabled?.(row)} onChange={() => toggleRow(row)} /> {selection.isRowDisabled?.(row) ? "Unavailable" : "Select"}</label></div> : null}
                {serialNumber ? (
                  <div className={styles.cardHeader}>
                    S.No. {offset + rowIndex + 1}
                  </div>
                ) : null}
                {mobileColumns.map((col) => (
                  <div key={col.id} className={styles.cardRow}>
                    <span className={clsx(styles.cardLabel, "sub-heading-xs")}>
                      {col.mobileLabel ?? col.header}
                    </span>
                    <div className={styles.cardValue}>
                      {renderCellContent(
                        col,
                        row,
                        rowIndex,
                        clsx(styles.truncated, styles.truncatedFull),
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {mobileSummary ? (
            <div className={styles.mobileSummary}>{mobileSummary}</div>
          ) : null}
          {showPaginationBar ? (
            <div
              className={styles.pagination}
              role="navigation"
              aria-label="Table pagination"
            >
              <div className={styles.paginationLeft}>
                {totalLabel ? (
                  <span className={styles.totalLabel}>{totalLabel}</span>
                ) : (
                  <span className={styles.paginationSpacer} aria-hidden />
                )}
              </div>
              <div className={styles.paginationCenter}>
                <button
                  type="button"
                  className={styles.pageButton}
                  disabled={!canPrev}
                  onClick={() => onPageChange(safePage - 1)}
                >
                  <ChevronLeft
                    className={styles.pageButtonIcon}
                    size={16}
                    strokeWidth={1.75}
                    aria-hidden
                  />
                  Previous
                </button>
                <span className={styles.pageMeta}>
                  Page {safePage} of {totalPages}
                </span>
                <button
                  type="button"
                  className={styles.pageButton}
                  disabled={!canNext}
                  onClick={() => onPageChange(safePage + 1)}
                >
                  Next
                  <ChevronRight
                    className={styles.pageButtonIcon}
                    size={16}
                    strokeWidth={1.75}
                    aria-hidden
                  />
                </button>
              </div>
              <div className={styles.paginationRight}>
                {onPageSizeChange ? (
                  <label className={styles.pageSize}>
                    <span className={styles.pageSizeLabel}>Rows per page</span>
                    <select
                      className={styles.pageSizeSelect}
                      value={String(pageSize)}
                      onChange={(e) => onPageSizeChange(Number(e.target.value))}
                      aria-label="Rows per page"
                    >
                      {pageSizeOptions.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <span className={styles.paginationSpacer} aria-hidden />
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}
